import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid'
import { Field, ErrorMessage } from 'formik'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { RichTextEditor } from '@/components/Editors/RichTextEditor'
import { FormikFieldProps } from '@/components/Editors/RichTextEditor/types'
import { ChatbotType } from '@/redux/types/configuration'
import { MessageSquare, History, Coins } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface BehaviorSectionProps {
  chatbotType: ChatbotType | null
  isGameCompleted: boolean
  openChatBoxesCount?: number
  isTestChatOpen?: boolean
  onTestChatClick?: () => void
  onPreviousChatsClick?: () => void
  hideTestChatControls?: boolean
  disabled?: boolean
  hideBehaviorTextFields?: boolean
}

export default function BehaviorSection({
  chatbotType,
  isGameCompleted,
  openChatBoxesCount = 0,
  isTestChatOpen = false,
  onTestChatClick,
  onPreviousChatsClick,
  hideTestChatControls = false,
  disabled = false,
  hideBehaviorTextFields = false,
}: BehaviorSectionProps) {
  const isRandr = chatbotType === ChatbotType.RANDR
  const isAdjudicator = chatbotType === ChatbotType.ADJUDICATION

  const getFieldOrder = () => {
    if (isRandr || isAdjudicator) {
      return ['prompt', 'greetings', 'scenario', 'restrictions']
    } else {
      return ['greetings', 'prompt', 'scenario', 'restrictions']
    }
  }

  const isDisabled = isGameCompleted || openChatBoxesCount !== 0 || disabled

  const handleCurrencyCostChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: any
  ) => {
    if (isDisabled) return
    const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
    field.onChange({
      target: {
        name: field.name,
        value: isNaN(value) ? 0 : value,
      },
    })
  }

  return (
    <div className='space-y-6'>
      <div>
        <div className='flex items-center'>
          <h2 className='text-2xl font-semibold text-foreground mb-2'>
            Configure Assistant Behavior
            {isRandr && (
              <span className='ml-2 text-lg font-normal text-blue-500'>
                (Event Analyst System Chatbot)
              </span>
            )}
            {isAdjudicator && (
              <span className='ml-2 text-lg font-normal text-blue-500'>
                (System Adjudication Chatbot)
              </span>
            )}
          </h2>

          {!hideTestChatControls && (
            <div className='ml-auto flex items-center gap-3'>
              <Button
                className='bg-dark-blue hover:bg-dark-blue-hovered'
                onClick={onPreviousChatsClick}
                type='button'
                disabled={isDisabled}
              >
                <span className='flex items-center gap-2'>
                  <History className='w-4 h-4 transition-transform duration-300 group-hover:-translate-x-0.5' />
                  Previous Chats
                </span>
              </Button>

              <Button
                className='bg-dark-blue hover:bg-dark-blue-hovered'
                onClick={onTestChatClick}
                type='button'
                disabled={isTestChatOpen || isDisabled}
              >
                <span className='flex items-center gap-2'>
                  <MessageSquare className='w-4 h-4 transition-transform duration-300 group-hover:rotate-12' />
                  Test Chat
                </span>
              </Button>
            </div>
          )}
        </div>
        {isGameCompleted && (
          <p className='mb-4 text-sm text-gray-500'>
            This game has been completed. The configuration can be viewed but
            not edited.
          </p>
        )}
      </div>
      <div className='space-y-6'>
        {hideBehaviorTextFields && (
          <p className='text-sm text-muted-foreground rounded-md border border-border bg-muted/40 p-3'>
            Behavioral text (greeting, prompt, scenario, restrictions and
            currency cost) are loaded from World Setup. Edit them under the
            room&apos;s World Setup page.
          </p>
        )}
        {!hideBehaviorTextFields &&
          getFieldOrder().map((fieldName) => (
          <div key={fieldName} aria-disabled={isDisabled}>
            <label
              htmlFor={fieldName}
              className='flex items-center gap-2 text-sm font-medium text-foreground mb-2'
            >
              {fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}
              <span className='text-xs text-red-500'>*</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <QuestionMarkCircleIcon className='w-4 h-4 text-muted-foreground' />
                </TooltipTrigger>
                <TooltipContent>
                  {fieldName === 'prompt' && (isRandr || isAdjudicator)
                    ? `Primary system prompt for ${isRandr ? 'Event Analyst' : 'Adjudication'} chatbot`
                    : `Enter your ${fieldName} here`}
                </TooltipContent>
              </Tooltip>
            </label>
            <Field name={fieldName}>
              {({ field, meta }: FormikFieldProps) => (
                <RichTextEditor
                  id={field.name}
                  name={field.name}
                  value={field.value || ''}
                  onChange={(value: string) => {
                    if (isDisabled) return
                    field.onChange({
                      target: {
                        name: field.name,
                        value,
                      },
                    })
                  }}
                  error={!!(meta.touched && meta.error)}
                  placeholder={`Enter your ${fieldName} here`}
                  className='mt-1'
                  disabled={isDisabled}
                />
              )}
            </Field>
            <ErrorMessage
              name={fieldName}
              component='div'
              className='mt-1 text-sm text-red-500 text-destructive-foreground'
            />
          </div>
        ))}

        {!hideBehaviorTextFields && (
        <div aria-disabled={isDisabled}>
          <Label
            htmlFor='currency_cost'
            className='flex items-center gap-2 text-sm font-medium text-foreground mb-2'
          >
            <Coins className='w-4 h-4' />
            Currency Cost
            <Tooltip>
              <TooltipTrigger asChild>
                <QuestionMarkCircleIcon className='w-4 h-4 text-muted-foreground' />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Fixed currency cost per query/interaction with this advisor.
                  This cost will be deducted from player currency limits when
                  they message this advisor.
                </p>
              </TooltipContent>
            </Tooltip>
          </Label>
          <Field name='currency_cost'>
            {({ field }: any) => (
              <div className='space-y-1'>
                <Input
                  id='currency_cost'
                  type='number'
                  min='0'
                  step='1'
                  value={field.value || ''}
                  onChange={(e) => handleCurrencyCostChange(e, field)}
                  onBlur={field.onBlur}
                  placeholder='0'
                  className='mt-1 max-w-xs'
                  disabled={isDisabled}
                />
                <p className='text-xs text-muted-foreground'>
                  Set to 0 for no cost. Only applies when resource limits are
                  enabled for the room.
                </p>
              </div>
            )}
          </Field>
          <ErrorMessage
            name='currency_cost'
            component='div'
            className='mt-1 text-sm text-red-500 text-destructive-foreground'
          />
        </div>
        )}
      </div>
    </div>
  )
}
